// mui
import { Button, Grid, Stack, Typography } from '@mui/material';
// form
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// little-state-machine
import { useStateMachine } from 'little-state-machine';
import { defaultOwnerOfficerObject } from '../../../../store/form/state/ownersOfficers';
// component
import { FormProvider, FieldCondition } from '../../../../components/hook-form';
import FormSectionControls from '../../FormSectionControls';
import Iconify from '../../../../components/iconify/Iconify';
// hooks
import useFormStateUpdateOnChange from '../../../../hooks/useFormStateUpdateOnChange';
import OwnerOfficerFields from './fields/OwnerOfficerFields';
// services
import formValidationSchema from '../../../../services/validation/formValidationSchema';

// -------------------------------------------------------

const Form = () => {
  const maxOwnersOfficers = 4;

  // state
  const { state } = useStateMachine();

  // form
  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(formValidationSchema),
    defaultValues: state.form,
  });

  const { watch, control, setValue } = methods;

  useFormStateUpdateOnChange(watch);

  const {
    fields: ownersOfficers,
    append: appendOwnerOfficer,
    remove: removeOwnerOfficer,
  } = useFieldArray({
    control,
    name: 'ownersOfficers',
    defaultValues: state.form.ownersOfficers,
  });

  const handleAppendOwnerOfficer = () => appendOwnerOfficer(defaultOwnerOfficerObject);
  const handleRemoveOwnerOfficer = (index) => (ownersOfficers?.length > 1 ? removeOwnerOfficer(index) : null);

  const handleClassCodeAnnualPayrollChange = (value, index) => {
    setValue(`ownersOfficers[${index}].payroll`, value);
  }

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            {ownersOfficers?.map(({ id, payroll }, idx) => (
              <OwnerOfficerFields
                onRemove={handleRemoveOwnerOfficer}
                isRemoveDisabled={ownersOfficers?.length === 1}
                index={idx}
                key={id}
                setPayrollInState={handleClassCodeAnnualPayrollChange}
                initialPayroll={payroll}
              />
            ))}
          </Stack>
        </Grid>
        <FieldCondition fieldName="ownersOfficers" condition={(val) => val.length < maxOwnersOfficers}>
          <Grid item xs={12}>
            <Stack alignItems={'flex-end'} direction={'column'}>
              <Button
                variant={'text'}
                onClick={handleAppendOwnerOfficer}
                endIcon={<Iconify icon="eva:plus-fill" width={25} height={25} />}
              >
                Add Owner / Officer
              </Button>
            </Stack>
          </Grid>
        </FieldCondition>
        <FieldCondition fieldName="ownersOfficers" condition={(val) => val.length >= maxOwnersOfficers}>
          <Grid item xs={12}>
            <Stack alignItems={'center'}>
              <Typography fontSize={15}>
                If more than {maxOwnersOfficers} owner/officers, please attach a list of all owner/officers.
              </Typography>
            </Stack>
          </Grid>
        </FieldCondition>
      </Grid>

      <FormSectionControls step={'owners-officers'} />
    </FormProvider>
  );
};

export default Form;
